import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import InfoTooltip from "./InfoTooltip";

interface PanelProps {
  /** Heading text. Omit for a header-less surface. */
  title?: ReactNode;
  /** Optional leading icon (Lucide), rendered before the title. */
  icon?: ReactNode;
  /** Optional pedagogy tooltip shown as an (i) next to the title. */
  info?: ReactNode;
  /** Right-aligned header content (counts, actions, badges). */
  aside?: ReactNode;
  /** Accessible label for the section landmark. */
  "aria-label"?: string;
  className?: string;
  /** Extra classes for the body wrapper. */
  bodyClassName?: string;
  /** When true, the body has no padding (e.g. flush tables). */
  flush?: boolean;
  /**
   * Visual emphasis (C3 — sober elevation hierarchy, no glow):
   * "accent" anchors the eye on the live focal panel (active cockpit),
   * "danger" marks a panel whose content is an active alarm.
   */
  tone?: "default" | "accent" | "danger";
  /** data-testid passthrough on the section landmark. */
  testId?: string;
  children?: ReactNode;
}

const TONE_CLASS: Record<NonNullable<PanelProps["tone"]>, string> = {
  default: "",
  accent:
    "border-info/40 ring-1 ring-info/20 shadow-[var(--shadow-glow-info)]",
  danger:
    "border-sell/50 ring-1 ring-sell/20 shadow-[var(--shadow-glow-sell)]",
};

/**
 * The standard dashboard/page surface: an elevated card with an optional
 * header row (title + (i) tooltip + right-aligned aside). Keeps the legacy
 * `panel-header` class as an inert selector hook for the test suite.
 */
export default function Panel({
  title,
  icon,
  info,
  aside,
  "aria-label": ariaLabel,
  className,
  bodyClassName,
  flush = false,
  tone = "default",
  testId,
  children,
}: PanelProps) {
  const hasHeader = title != null || aside != null;
  return (
    <section
      aria-label={ariaLabel}
      data-testid={testId}
      data-tone={tone === "default" ? undefined : tone}
      className={cn(
        "glass bezel text-card-foreground border-border/80 relative flex flex-col overflow-hidden rounded-xl border",
        "before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/15 before:to-transparent",
        TONE_CLASS[tone],
        className,
      )}
    >
      {hasHeader && (
        <header className="panel-header border-border/70 flex items-center justify-between gap-3 border-b bg-white/[0.015] px-4 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            {icon && (
              <span className="text-muted-foreground flex shrink-0 items-center [&_svg]:size-4">
                {icon}
              </span>
            )}
            {title != null && (
              <h2 className="text-foreground truncate text-sm font-semibold tracking-wide uppercase [letter-spacing:0.04em]">
                {title}
              </h2>
            )}
            {info != null && <InfoTooltip>{info}</InfoTooltip>}
          </div>
          {aside != null && (
            <div className="text-muted-foreground flex shrink-0 items-center gap-2 text-xs">
              {aside}
            </div>
          )}
        </header>
      )}
      <div className={cn(!flush && "p-4", bodyClassName)}>{children}</div>
    </section>
  );
}
